import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Check, SkipForward, CheckCircle2 } from "lucide-react";
import QuizReport from "@/components/quiz/QuizReport";

// --- Types ---
interface ReportStep { number: number; title: string; content: string; manual_time: string; benchmark: string; }
interface ReportPhase { phase: string; tasks: string[]; }
interface ReportAction { number: number; title: string; description: string; }
interface ReportSection { id: string; title: string; content?: string; steps?: ReportStep[]; phases?: ReportPhase[]; actions?: ReportAction[]; }
interface Report { plan_title: string; situation_summary: string; point_a: string; point_b: string; sections: ReportSection[]; }

type QuestionType = "single" | "multi" | "text" | "textarea";

interface QuizQuestion {
  id: string;
  type: QuestionType;
  label: string;
  subtitle: string;
  placeholder?: string;
  options?: string[];
  conditional?: { questionId: string; notEquals: string };
  skippable?: boolean;
  chips?: string[];
}

const questions: QuizQuestion[] = [
  { id: "stage", type: "single", label: "Where are you right now?", subtitle: "Pick the one that best describes your situation today.", options: ["I'm just starting out — no audience yet", "I'm posting consistently but not getting much traction", "I have a growing audience but I'm not making money from it", "I'm already making some money but I want to scale"] },
  { id: "niche", type: "text", label: "What's your niche?", subtitle: "Be as specific as possible — this helps us build a more accurate plan for you.", placeholder: "e.g. personal finance, fitness, travel, parenting, business..." },
  { id: "platforms", type: "multi", label: "Which platforms are you active on?", subtitle: "Select all that apply.", options: ["TikTok", "Instagram Reels", "YouTube (long form)", "YouTube Shorts", "Podcast", "Newsletter / Blog", "X (Twitter)", "LinkedIn"] },
  { id: "audience_size", type: "single", label: "How big is your current audience?", subtitle: "Across all platforms combined.", options: ["Under 500", "500 – 5,000", "5,000 – 20,000", "20,000 – 100,000", "Over 100,000"] },
  { id: "posting_frequency", type: "single", label: "How often are you posting?", subtitle: "Your current posting cadence.", options: ["Rarely (less than once a week)", "1–3 times per week", "4–6 times per week", "Daily or more"] },
  { id: "monetisation_history", type: "single", label: "Have you tried to monetise before?", subtitle: "Be honest — this shapes your roadmap.", options: ["No, I haven't tried yet", "Yes, I've tried but it didn't work", "Yes, I'm making a little money but it's inconsistent", "Yes, I have a product/service but I want to grow it"] },
  { id: "what_they_tried", type: "textarea", label: "What did you try?", subtitle: "Be honest — this helps us understand what went wrong and what to do differently.", placeholder: "e.g. I tried selling an ebook but only made 2 sales...", conditional: { questionId: "monetisation_history", notEquals: "No, I haven't tried yet" }, skippable: true },
  { id: "blocker", type: "textarea", label: "What's your biggest blocker right now?", subtitle: "There are no wrong answers. The more specific you are, the better your plan will be.", placeholder: "e.g. I don't know what to sell, I don't have time...", skippable: true, chips: ["Time", "Don't know what to sell", "Small audience", "Tech overwhelm", "Fear of selling"] },
  { id: "goal", type: "single", label: "What does success look like for you in the next 6 months?", subtitle: "Pick the one that resonates most.", options: ["Build a loyal, engaged audience in my niche", "Launch my first digital product and make my first sale", "Replace my 9–5 income with content revenue", "Scale an existing income stream to £5k/month or more", "Build an email list of 1,000+ subscribers", "Launch a paid community or membership"] },
  { id: "hours_per_week", type: "single", label: "How much time can you realistically commit per week?", subtitle: "Be realistic — we'll build your timeline around this.", options: ["Less than 2 hours", "2–5 hours", "5–10 hours", "10–20 hours", "20+ hours"] },
  { id: "name", type: "text", label: "What's your first name?", subtitle: "We'll use this to personalise your report.", placeholder: "Your first name" },
  { id: "biggest_fear", type: "textarea", label: "What's your biggest fear about monetising?", subtitle: "This is important — most creators share the same fears.", placeholder: "e.g. I'm scared no one will buy, I don't want to seem salesy...", skippable: true, chips: ["No one will buy", "Seeming salesy", "Audience too small", "Not good enough", "Pricing it wrong"] },
];

const loadingMessages = [
  { text: "Analysing your situation...", delay: 0 },
  { text: "Building your roadmap...", delay: 3000 },
  { text: "Personalising recommendations...", delay: 7000 },
  { text: "Mapping your timeline...", delay: 11000 },
  { text: "Finalising your report...", delay: 15000 },
];

export default function Quiz() {
  const navigate = useNavigate();
  const getVisibleQuestions = () => questions.filter((q) => {
    if (!q.conditional) return true;
    const depAnswer = answers[q.conditional.questionId];
    return typeof depAnswer === "string" && depAnswer !== q.conditional.notEquals;
  });

  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [currentQ, setCurrentQ] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [shakeError, setShakeError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [animKey, setAnimKey] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [genError, setGenError] = useState("");
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [email, setEmail] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [bounceNext, setBounceNext] = useState(false);

  useEffect(() => {
    if (!generating) { setCompletedSteps([]); return; }
    const timers = loadingMessages.map((msg, i) =>
      setTimeout(() => setCompletedSteps(prev => [...prev, i]), msg.delay + 1500)
    );
    return () => timers.forEach(clearTimeout);
  }, [generating]);

  const visibleQuestions = getVisibleQuestions();
  const q = visibleQuestions[currentQ];
  const progressPct = ((currentQ + 1) / visibleQuestions.length) * 100;

  const isAnswered = () => {
    if (!q) return false;
    const val = answers[q.id];
    if (!val) return false;
    if (Array.isArray(val)) return val.length > 0;
    return typeof val === "string" && val.trim().length > 0;
  };

  // Trigger bounce when answer is given
  useEffect(() => {
    if (isAnswered() && q?.type === "single") {
      setBounceNext(true);
      const t = setTimeout(() => setBounceNext(false), 600);
      return () => clearTimeout(t);
    }
  }, [answers, currentQ]);

  const transition = (cb: () => void) => {
    setFadeIn(false);
    setTimeout(() => { cb(); setAnimKey(k => k + 1); setFadeIn(true); }, 180);
  };

  const handleNext = () => {
    if (!isAnswered()) {
      setShakeError(true); setErrorMsg("Please answer this question to continue");
      setTimeout(() => setShakeError(false), 500); return;
    }
    setErrorMsg("");
    if (currentQ < visibleQuestions.length - 1) transition(() => setCurrentQ(p => p + 1));
    else generateReport();
  };

  const handleSkip = () => {
    setErrorMsg("");
    if (!answers[q.id]) setAnswers(p => ({ ...p, [q.id]: "" }));
    if (currentQ < visibleQuestions.length - 1) transition(() => setCurrentQ(p => p + 1));
    else generateReport();
  };

  const handleBack = () => { if (currentQ > 0) { setErrorMsg(""); transition(() => setCurrentQ(p => p - 1)); } };
  const setSingle = (val: string) => setAnswers(p => ({ ...p, [q.id]: val }));
  const toggleMulti = (val: string) => {
    setAnswers(p => {
      const curr = (p[q.id] as string[]) || [];
      return { ...p, [q.id]: curr.includes(val) ? curr.filter(v => v !== val) : [...curr, val] };
    });
  };
  const setText = (val: string) => setAnswers(p => ({ ...p, [q.id]: val }));
  const handleChipClick = (chip: string) => {
    const current = (answers[q.id] as string) || "";
    setText(current.trim().length === 0 ? chip : current + ", " + chip);
  };

  const generateReport = async () => {
    setGenerating(true); setGenError(""); setCompletedSteps([]);
    try {
      const { data, error } = await supabase.functions.invoke("generate-creator-plan", {
        body: { name: answers.name, stage: answers.stage, niche: answers.niche, platforms: answers.platforms, audience_size: answers.audience_size, posting_frequency: answers.posting_frequency, monetisation_history: answers.monetisation_history, what_they_tried: answers.what_they_tried || "", blocker: answers.blocker, goal: answers.goal, hours_per_week: answers.hours_per_week, biggest_fear: answers.biggest_fear },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setReport(data.plan);
    } catch (err: any) {
      console.error("Quiz generateReport error:", err);
      setGenError(err?.message || "Something went wrong generating your report. Please try again.");
    } finally { setGenerating(false); }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nameVal = ((answers.name as string) || "").trim();
    if (!email.trim()) return;
    const emailVal = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) { toast.error("Please enter a valid email address."); return; }
    setEmailSubmitting(true);
    try {
      const { error: wlError } = await supabase.from("waitlist").insert({ name: nameVal || "Creator", email: emailVal, source: "quiz" });
      if (wlError && wlError.code !== "23505") throw wlError;
      supabase.from("creator_plans" as any).insert({ email: emailVal, name: nameVal, stage: answers.stage, niche: answers.niche, platforms: answers.platforms, audience_size: answers.audience_size, posting_frequency: answers.posting_frequency, monetisation_history: answers.monetisation_history, what_they_tried: answers.what_they_tried || null, blocker: answers.blocker, goal: answers.goal, hours_per_week: answers.hours_per_week, biggest_fear: answers.biggest_fear, plan_json: report, full_report_json: report } as any).then(({ error }) => { if (error) console.warn("creator_plans insert error:", error); });
      const LAUNCH_DATE = new Date("2025-04-20T09:00:00Z");
      const now = new Date();
      const sequenceType = now >= LAUNCH_DATE ? "postlaunch" : "prelaunch";
      supabase.from("email_sequence_queue" as any).insert({ email: emailVal, first_name: nameVal || "Creator", subscribed_at: now.toISOString(), sequence_step: 1, sequence_type: sequenceType, next_send_at: now.toISOString(), sent: false } as any).then(({ error: seqError }: any) => { if (seqError && seqError.code !== "23505") console.warn("email_sequence_queue insert error:", seqError); });
      
      // Compute score data for the special offer page
      const totalScore = report?.sections?.length || 0;
      const overallGrade = "B"; // simplified
      
      // Redirect to special offer instead of unblurring
      navigate(`/special-offer?email=${encodeURIComponent(emailVal)}&name=${encodeURIComponent(nameVal)}`);
    } catch (err: any) {
      console.error("Quiz: handleEmailSubmit error:", err);
      toast.error("Something went wrong. Please try again.");
      setEmailSubmitting(false);
    }
  };

  // --- Loading ---
  if (generating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: "#FAFAFA" }}>
        <Loader2 className="w-12 h-12 animate-spin mb-8" style={{ color: "#7C3AED" }} />
        <h2 className="text-xl md:text-2xl font-bold mb-6 text-center" style={{ color: "#111827" }}>
          Building your personalised report...
        </h2>
        <div className="w-full max-w-sm space-y-3">
          {loadingMessages.map((msg, i) => {
            const isDone = completedSteps.includes(i);
            return (
              <div key={i} className="flex items-center gap-3 transition-all duration-300" style={{ opacity: isDone ? 1 : 0.35 }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all" style={{ background: isDone ? "#DCFCE7" : "#F3F4F6" }}>
                  {isDone ? <Check className="w-3.5 h-3.5 text-green-600" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                </div>
                <span className="text-sm" style={{ color: "#374151" }}>{msg.text}</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs mt-8" style={{ color: "#9CA3AF" }}>This usually takes 15–30 seconds</p>
      </div>
    );
  }

  // --- Error ---
  if (genError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: "#FAFAFA" }}>
        <p className="text-lg mb-4 text-center" style={{ color: "#111827" }}>{genError}</p>
        <button onClick={generateReport} className="font-bold px-8 py-3 rounded-xl border-none text-white cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: "#7C3AED" }}>
          Try Again
        </button>
      </div>
    );
  }

  // --- Report ---
  if (report) {
    return <QuizReport report={report} unlocked={unlocked} email={email} setEmail={setEmail} emailSubmitting={emailSubmitting} handleEmailSubmit={handleEmailSubmit} answers={answers} />;
  }

  // --- Quiz ---
  if (!q) return null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FAFAFA", color: "#111827" }}>
      <Helmet>
        <title>Build Your Roadmap | AI StoryCraft Studio</title>
        <meta name="description" content="Take the quiz to get your personalised creator monetisation plan." />
      </Helmet>
      {/* Progress bar */}
      <div className="w-full px-4 pt-5 pb-2">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium" style={{ color: "#6B7280" }}>Step {currentQ + 1} of {visibleQuestions.length}</p>
            <p className="text-sm font-bold" style={{ color: "#7C3AED" }}>{Math.round(progressPct)}%</p>
          </div>
          <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: "#E5E7EB" }}>
            <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg, #7C3AED 0%, #F59E0B 100%)" }} />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex items-start md:items-center justify-center px-4 pt-6 md:pt-0 pb-8" style={{ minHeight: "70vh" }}>
        <div
          key={animKey}
          className={`w-full max-w-2xl bg-white rounded-2xl p-6 md:p-8 shadow-md quiz-slide-in ${shakeError ? "animate-shake" : ""}`}
          style={{ opacity: fadeIn ? 1 : 0, transition: "opacity 0.18s", border: "1px solid #E5E7EB" }}
        >
          {/* Step badge */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: "#F3F0FF", color: "#7C3AED" }}>
              {currentQ + 1}
            </div>
            <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#9CA3AF" }}>
              Step {currentQ + 1} of {visibleQuestions.length}
            </span>
          </div>

          <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ color: "#111827" }}>{q.label}</h2>
          {q.subtitle && <p className="text-sm mb-6" style={{ color: "#6B7280" }}>{q.subtitle}</p>}

          {/* Single select */}
          {q.type === "single" && (
            <div className="space-y-3">
              {q.options!.map(opt => {
                const selected = answers[q.id] === opt;
                return (
                  <button key={opt} onClick={() => setSingle(opt)}
                    className="w-full text-left px-4 py-3 rounded-xl border transition-all text-sm cursor-pointer flex items-center gap-3 duration-200 active:scale-[0.97]"
                    style={{
                      background: selected ? "#7C3AED" : "#FFFFFF",
                      borderColor: selected ? "#7C3AED" : "#E5E7EB",
                      color: selected ? "#FFFFFF" : "#374151",
                    }}
                  >
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all" style={{ borderColor: selected ? "#FFFFFF" : "#D1D5DB", backgroundColor: selected ? "#FFFFFF" : "transparent" }}>
                      {selected && <CheckCircle2 className="w-4 h-4" style={{ color: "#7C3AED" }} />}
                    </div>
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* Multi select */}
          {q.type === "multi" && (
            <div className="grid grid-cols-2 gap-3">
              {q.options!.map(opt => {
                const selected = ((answers[q.id] as string[]) || []).includes(opt);
                return (
                  <button key={opt} onClick={() => toggleMulti(opt)}
                    className="text-left px-4 py-3 rounded-xl border transition-all text-sm cursor-pointer flex items-center gap-2 duration-200 active:scale-[0.97]"
                    style={{
                      background: selected ? "#7C3AED" : "#FFFFFF",
                      borderColor: selected ? "#7C3AED" : "#E5E7EB",
                      color: selected ? "#FFFFFF" : "#374151",
                    }}
                  >
                    <div className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0" style={{ borderColor: selected ? "#FFFFFF" : "#D1D5DB", backgroundColor: selected ? "#FFFFFF" : "transparent" }}>
                      {selected && <Check className="w-3 h-3" style={{ color: "#7C3AED" }} />}
                    </div>
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* Text input */}
          {q.type === "text" && (
            <input
              value={(answers[q.id] as string) || ""}
              onChange={e => setText(e.target.value)}
              placeholder={q.placeholder}
              className="w-full text-base py-3 px-0 bg-transparent outline-none transition-colors duration-200"
              style={{ borderBottom: "2px solid #E5E7EB", color: "#111827" }}
              onFocus={e => (e.target.style.borderBottomColor = "#7C3AED")}
              onBlur={e => (e.target.style.borderBottomColor = "#E5E7EB")}
            />
          )}

          {/* Textarea */}
          {q.type === "textarea" && (
            <>
              <textarea
                value={(answers[q.id] as string) || ""}
                onChange={e => setText(e.target.value)}
                placeholder={q.placeholder}
                rows={3}
                className="w-full text-base py-3 px-0 bg-transparent outline-none resize-none transition-colors duration-200"
                style={{ borderBottom: "2px solid #E5E7EB", color: "#111827" }}
                onFocus={e => (e.target.style.borderBottomColor = "#7C3AED")}
                onBlur={e => (e.target.style.borderBottomColor = "#E5E7EB")}
              />
              {q.chips && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {q.chips.map(chip => (
                    <button key={chip} onClick={() => handleChipClick(chip)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95"
                      style={{ backgroundColor: "#F3F0FF", border: "1px solid #E9E3FF", color: "#7C3AED" }}
                    >{chip}</button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Lock className="w-3.5 h-3.5" style={{ color: "#9CA3AF" }} />
                <span className="text-xs" style={{ color: "#9CA3AF" }}>Your answers are private</span>
              </div>
            </>
          )}

          {errorMsg && <p className="text-sm mt-3 text-red-500">{errorMsg}</p>}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {currentQ > 0 ? (
              <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm bg-transparent border-none cursor-pointer transition-colors hover:text-gray-700" style={{ color: "#9CA3AF" }}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            <div className="flex items-center gap-3">
              {q.skippable && (
                <button onClick={handleSkip} className="inline-flex items-center gap-1.5 text-sm bg-transparent border-none cursor-pointer" style={{ color: "#9CA3AF" }}>
                  <SkipForward className="w-3.5 h-3.5" /> Skip
                </button>
              )}
              <button onClick={handleNext}
                className={`font-bold px-6 py-2.5 rounded-xl border-none text-white inline-flex items-center gap-1.5 cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${bounceNext ? "quiz-bounce-next" : ""}`}
                style={{ backgroundColor: "#7C3AED" }}
              >
                {currentQ === visibleQuestions.length - 1 ? "Build My Report →" : <>Next <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
